import React, { useState, useEffect, useMemo, useCallback, Suspense, useRef, useLayoutEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenancy } from '@/contexts/TenancyContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Bar, BarChart, XAxis, YAxis, RadialBarChart, RadialBar, PolarAngleAxis, LabelList, PieChart, Pie, Cell, Label as RechartsLabel, Rectangle } from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Search, Download, RefreshCw, Check } from 'lucide-react';
import './soa.css';

// Firebase imports
import { findFrameworkName } from '@/js/external-js/variableModules/utils/findFrameworkName.util';

// SOA service import
import { getSOAData, updateControlStatus } from '@/services/soaService';
import {
  Control,
  Framework,
  ImplementationStatus,
  FrameworkStats,
  SOAData,
  emptySOAData
} from '@/types/soa';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { DataTable } from '@/components/ui/datatable';
import { getSOAColumns } from './components/columns';
import { SOAVersionHistoryModal } from './components/SOAVersionHistoryModal';

// Define Firebase types based on the API being used
interface DocumentReference {
  get(): Promise<DocumentSnapshot>;
  set(data: any, options?: { merge?: boolean }): Promise<void>;
}

interface DocumentSnapshot {
  exists: boolean;
  data(): any;
}

// Extend Window interface to include companyID
declare global {
  interface Window {
    companyID?: string;
  }
}


// Main fetch SOA data function that uses our service layer
const fetchSOAData = async (tenancyId?: string, isModuleEnabled?: (moduleName: string) => boolean): Promise<SOAData> => {
  try {
    const companyID = tenancyId;

    // Use our service to get the SOA data
    const serviceData = await getSOAData(companyID, isModuleEnabled);

    // If no controls were found, use empty data object
    if (!serviceData || serviceData.controls.length === 0) {
      return emptySOAData;
    }

    // Service data types now directly match component types
    return serviceData;
  } catch (error) {
    console.error('Error fetching SOA data:', error);
    return emptySOAData; // Return empty data on error instead of throwing
  }
};

// Control Edit Modal Component
const ControlEditModal = ({ control, isOpen, onClose, onSave, isReadOnly = false }: { control: Control | null; isOpen: boolean; onClose: () => void; onSave: (updatedControl: Control) => Promise<void>; isReadOnly?: boolean }) => {
  const [editedControl, setEditedControl] = useState<Control | null>(null);
  const [saving, setSaving] = useState(false);
  const { activeTenancy } = useTenancy();
  const controlIds = useMemo(() => (editedControl?.controlID ? [String(editedControl.controlID)] : []), [editedControl?.controlID]);

  useEffect(() => {
    if (control) {
      setEditedControl({ ...control });
    }
  }, [control]);

  if (!editedControl) return null;

  const handleSave = async () => {
    if (!editedControl || saving) return;
    setSaving(true);
    try {
      await onSave(editedControl);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">{isReadOnly ? 'View' : 'Edit'} Control: {editedControl.controlID}</DialogTitle>
          </div>

          <div className="px-4 pb-4">
            <div className="grid gap-4 pt-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="control-name" className="text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  Name
                </Label>
                <Input
                  id="control-name"
                  value={editedControl.name}
                  readOnly
                  className="col-span-3 border border-gray-200 dark:border-gray-700 rounded-md focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="control-description" className="text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  Description
                </Label>
                <Textarea
                  id="control-description"
                  value={editedControl.description}
                  onChange={(e) => !isReadOnly && setEditedControl({ ...editedControl, description: e.target.value })}
                  readOnly={isReadOnly}
                  className="col-span-3 border border-gray-200 dark:border-gray-700 rounded-md focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-sm font-medium text-gray-600 dark:text-gray-300">Applicable</Label>
                <div className="col-span-3 flex items-center space-x-2">
                  <Checkbox
                    id="control-applicable"
                    checked={editedControl.applicable}
                    onCheckedChange={(checked) => !isReadOnly && setEditedControl({ ...editedControl, applicable: !!checked })}
                    disabled={isReadOnly}
                    className="text-primary focus:ring-primary"
                  />
                  <label htmlFor="control-applicable" className="text-sm font-medium leading-none text-gray-700 dark:text-gray-300 peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Control is applicable
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="control-justification" className="text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  Justification
                </Label>
                <Textarea
                  id="control-justification"
                  value={editedControl.justification}
                  onChange={(e) => !isReadOnly && setEditedControl({ ...editedControl, justification: e.target.value })}
                  readOnly={isReadOnly}
                  className="col-span-3 border border-gray-200 dark:border-gray-700 rounded-md focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="control-owner" className="text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  Owner
                </Label>
                {/* <Input
                  id="control-owner"
                  value={editedControl.owner || ''}
                  onChange={(e) => setEditedControl({ ...editedControl, owner: e.target.value })}
                  className="col-span-3 border border-gray-200 dark:border-gray-700 rounded-md focus:border-primary focus:ring-1 focus:ring-primary"
                /> */}
                <SingleUserCombobox
                  selected={editedControl.owner}
                  setSelected={(value) => !isReadOnly && setEditedControl({ ...editedControl, owner: value })}
                  readOnly={isReadOnly}
                  className="col-span-3 border border-gray-200 dark:border-gray-700 rounded-md focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="implementation-status" className="text-right text-sm font-medium text-gray-600 dark:text-gray-300">
                  Status
                </Label>
                <Select
                  value={editedControl.status}
                  onValueChange={(value: Control['status']) => {
                    if (isReadOnly) return;
                    const isApplicable = !(value === 'Not applicable' || value === 'Unknown');
                    setEditedControl(prev => ({
                      ...prev,
                      ...editedControl,
                      status: value as Control['status'],
                      applicable: isApplicable
                    }));
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="col-span-3 border border-gray-200 dark:border-gray-700 rounded-md focus:border-primary focus:ring-1 focus:ring-primary w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fully implemented">Fully implemented</SelectItem>
                    <SelectItem value="Partially implemented">Partially implemented</SelectItem>
                    <SelectItem value="Not implemented">Not implemented</SelectItem>
                    <SelectItem value="Not applicable">Not applicable</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* MCM Mappings for this control */}
            <div className="mt-4">
              <MCMMappingsCard
                tenancyId={activeTenancy?.id}
                isReadOnly={isReadOnly}
                controlIds={controlIds}
                defaultEntityLink={{
                  id: editedControl?.id || editedControl?.controlID,
                  ref: editedControl?.controlID,
                  title: editedControl?.name || editedControl?.controlID || '',
                  domain: 'Control',
                  relation: 'implements',
                }}
                collapsible
              />
            </div>
          </div>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t gap-2">
            <Button variant="outline" onClick={onClose} className="border border-gray-200 dark:border-gray-700 hover:bg-gray-50 hover:text-black dark:hover:text-white dark:hover:bg-gray-800">
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Framework Name Component with memoization
const FrameworkName: React.FC<{
  frameworkId: string;
  frameworks: Framework[];
  onNameResolved?: (id: string, name: string) => void;
}> = React.memo(({ frameworkId, frameworks, onNameResolved }) => {
  const [name, setName] = useState<string>(frameworkId);
  const queryClient = useQueryClient();

  // Memoize the cache key to prevent recreation on each render
  const cacheKey = useMemo(() => ['framework-name', frameworkId], [frameworkId]);

  // Track if we've already called onNameResolved to prevent infinite loops
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    // Reset the resolved flag when frameworkId changes
    hasResolvedRef.current = false;
  }, [frameworkId]);

  useEffect(() => {
    const fetchName = async () => {
      try {
        // Check if we have the name in cache first
        const cachedName = queryClient.getQueryData<string>(cacheKey);
        if (cachedName) {
          setName(cachedName);
          // Only call onNameResolved if we haven't already for this frameworkId
          if (onNameResolved && !hasResolvedRef.current) {
            hasResolvedRef.current = true;
            onNameResolved(frameworkId, cachedName);
          }
          return;
        }

        // Try to get the name from the findFrameworkName utility
        const frameworkName = (await findFrameworkName(frameworkId)) || (await findFrameworkName(frameworkId, true));
        if (frameworkName) {
          setName(frameworkName);
          queryClient.setQueryData(cacheKey, frameworkName);
          // Only call onNameResolved if we haven't already for this frameworkId
          if (onNameResolved && !hasResolvedRef.current) {
            hasResolvedRef.current = true;
            onNameResolved(frameworkId, frameworkName);
          }
          return;
        }

        // If not found, try to get it from the frameworks list
        const framework = frameworks.find((f) => f.id === frameworkId);
        if (framework?.name) {
          // Check if we can find a proper framework name using this + findFrameworkName otherwise use what we have
          const frameworkName = (await findFrameworkName(framework.name)) || (await findFrameworkName(framework.name, true));
          if (frameworkName) {
            setName(frameworkName);
            queryClient.setQueryData(cacheKey, frameworkName);
            // Only call onNameResolved if we haven't already for this frameworkId
            if (onNameResolved && !hasResolvedRef.current) {
              hasResolvedRef.current = true;
              onNameResolved(frameworkId, frameworkName);
            }
          } else {
            // If we can't find a proper name, use the framework name from the list
            setName(framework.name);
            queryClient.setQueryData(cacheKey, framework.name);
            // Only call onNameResolved if we haven't already for this frameworkId
            if (onNameResolved && !hasResolvedRef.current) {
              hasResolvedRef.current = true;
              onNameResolved(frameworkId, framework.name);
            }
          }
          return;
        }

        // Fallback to the ID
        setName(frameworkId);
        queryClient.setQueryData(cacheKey, frameworkId);
        // Only call onNameResolved if we haven't already for this frameworkId
        if (onNameResolved && !hasResolvedRef.current) {
          hasResolvedRef.current = true;
          onNameResolved(frameworkId, frameworkId);
        }
      } catch (error) {
        console.error('Error fetching framework name:', error);
        // Fallback to the framework ID or frameworks list
        const framework = frameworks.find((f) => f.id === frameworkId);
        const fallbackName = framework?.name || frameworkId;
        setName(fallbackName);
        queryClient.setQueryData(cacheKey, fallbackName);
        // Only call onNameResolved if we haven't already for this frameworkId
        if (onNameResolved && !hasResolvedRef.current) {
          hasResolvedRef.current = true;
          onNameResolved(frameworkId, fallbackName);
        }
      }
    };

    fetchName();
  }, [frameworkId, frameworks, onNameResolved, queryClient, cacheKey]);

  return <span className="break-words">{name}</span>;
});

// Add display name for React.memo component
FrameworkName.displayName = 'FrameworkName';

// Main SOA component
interface SOAProps {
  isReadOnly?: boolean;
}

const SOA: React.FC<SOAProps> = ({ isReadOnly = false }) => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const { activeTenancy, isModuleEnabled, enabledModules, userIdToName } = useTenancy();
  const { isDark } = useTheme();
  const tenancyId = activeTenancy?.id || window.companyID;

  // State for filters
  const [framework, setFramework] = useState<string | null>(null);
  const [implementationStatus, setImplementationStatus] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasHandledControlParam, setHasHandledControlParam] = useState(false);

  // Store resolved framework names
  const [resolvedFrameworkNames, setResolvedFrameworkNames] = useState<Map<string, string>>(new Map());

  // Track if framework names are being loaded
  const [loadingFrameworkNames, setLoadingFrameworkNames] = useState(false);

  // State for mobile view
  const [isMobile, setIsMobile] = useState(false);

  // State for control edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedControl, setSelectedControl] = useState<Control | null>(null);

  // State for version history modal
  const [versionHistoryModalOpen, setVersionHistoryModalOpen] = useState(false);

  // DED-1109: State for DataTable pagination to preserve page across data refreshes
  const [tablePageIndex, setTablePageIndex] = useState(0);

  // State for refresh loading
  const [refreshLoading, setRefreshLoading] = useState(false);
  // Overview grid state: show first row then allow expanding
  const [showAllOverviewFrameworks, setShowAllOverviewFrameworks] = useState(false);
  const [overviewFirstRowCount, setOverviewFirstRowCount] = useState(0);
  const overviewGridRef = useRef<HTMLDivElement | null>(null);
  const overviewWrapperRef = useRef<HTMLDivElement | null>(null);
  const [overviewAnimatedHeight, setOverviewAnimatedHeight] = useState<number>(0);
  // Track which Overview tab is active so we can re-measure when Donuts becomes visible
  const [overviewTabValue, setOverviewTabValue] = useState<'donut' | 'bar'>('donut');
  // Ensure we only run initial collapse fix once on first Donuts activation
  const hasInitializedDonutRef = useRef(false);
  const hasAutoSyncedRef = useRef(false);

  // Check if the screen is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Query client for prefetching
  const queryClient = useQueryClient();

  // Only fetch data when we have a valid tenancyId and isModuleEnabled is available
  const shouldFetchData = !!tenancyId && !!isModuleEnabled;

  // Prefetch SOA data when tenancy is ready
  useEffect(() => {
    if (!tenancyId || tenancyId === "Loading" || tenancyId === "Loading..." || tenancyId === "" || enabledModules.length === 0) return;
    if (shouldFetchData && !queryClient.getQueryData(['soaData', tenancyId])) {
      console.log('[SOA] Prefetching SOA data for tenancy:', tenancyId);
      console.log('[SOA] Available modules:', enabledModules);
      queryClient.prefetchQuery({
        queryKey: ['soaData', tenancyId],
        queryFn: async () => {
          console.log('[SOA] Executing prefetch query with isModuleEnabled:', isModuleEnabled('ISO 27001'));
          const data = await fetchSOAData(tenancyId, isModuleEnabled);
          console.log('[SOA] Fetched SOA data:', data);
          return data;
        }
      });
    }
  }, [tenancyId, queryClient, shouldFetchData, isModuleEnabled, enabledModules]);

  // Fetch SOA data with optimized caching
  const {
    data: soaData,
    error,
    isLoading,
    refetch
  } = useQuery<SOAData | null, Error>({
    queryKey: ['soaData', tenancyId],
    queryFn: async () => {
      if (!tenancyId || tenancyId === "Loading" || tenancyId === "Loading..." || tenancyId === "" || enabledModules.length === 0) {
        console.log('[SOA] Query conditions not met, returning null');
        return null;
      }
      setRefreshLoading(true);
      console.log('[SOA] Fetching SOA data with isModuleEnabled for ISO 27001:', isModuleEnabled('ISO 27001'));
      try {
        const data = await fetchSOAData(tenancyId, isModuleEnabled);
        console.log('[SOA] Fetched SOA data:', data);
        setRefreshLoading(false);
        return data;
      } catch (error) {
        console.error('[SOA] Error fetching SOA data:', error);
        setRefreshLoading(false);
        throw error; // Let React Query handle the error
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    enabled: shouldFetchData, // Only enable the query when we have a valid tenancy and isModuleEnabled
    retry: 2 // Retry failed requests twice before showing an error
  });

  // Memoize the data to prevent unnecessary re-renders, falling back to emptySOAData if soaData is null
  const data = useMemo(() => soaData || emptySOAData, [soaData]);

  // Handle loading and error states
  const renderContent = () => {
    if (isLoading || loadingFrameworkNames) {
      return <SOASkeletonLoader />;
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="bg-destructive/10 p-6 rounded-lg max-w-md">
            <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Data</h2>
            <p className="text-muted-foreground mb-4">{(error as Error).message}</p>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="bg-muted p-6 rounded-lg max-w-md">
            <h2 className="text-xl font-semibold mb-2">No Data Available</h2>
            <p className="text-muted-foreground mb-4">There is no Statement of Applicability data available for this tenancy.</p>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={`page-container ${isDark ? 'dark' : ''}`}>
        {/* Rest of your component JSX */}
      </div>
    );
  };

  // Handle framework name resolution
  const handleFrameworkNameResolved = (id: string, name: string) => {
    setResolvedFrameworkNames((prev) => {
      const newMap = new Map(prev);
      newMap.set(id, name);
      return newMap;
    });
  };

  // Preload framework names when data changes
  useEffect(() => {
    if (!data?.frameworks) return;

    setLoadingFrameworkNames(true);

    // This will be updated by the FrameworkName components as they resolve names
    setResolvedFrameworkNames(new Map());

    // Set a timeout to stop the loading state even if not all names are resolved
    const timer = setTimeout(() => {
      setLoadingFrameworkNames(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [data?.frameworks]);

  // Compute how many framework cards fit in the first row of the Overview grid
  const recomputeOverviewColumns = useCallback(() => {
    const container = overviewGridRef.current;
    if (!container) return;
    const style = window.getComputedStyle(container);
    const gap = parseFloat(style.columnGap || '16') || 16;
    const minCardWidth = 350;
    const containerWidth = container.clientWidth;
    const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
    setOverviewFirstRowCount(columns);
  }, []);

  // Measure heights for animation
  const measureCollapsedHeight = useCallback(() => {
    const grid = overviewGridRef.current;
    if (!grid) return 0;
    const style = window.getComputedStyle(grid);
    const paddingTop = parseFloat(style.paddingTop || '0') || 0;
    const paddingBottom = parseFloat(style.paddingBottom || '0') || 0;
    const children = Array.from(grid.children) as HTMLElement[];
    const count = overviewFirstRowCount || (isMobile ? 1 : 3);
    const firstRowChildren = children.slice(0, Math.min(children.length, count));
    let maxChildHeight = 0;
    for (const el of firstRowChildren) {
      maxChildHeight = Math.max(maxChildHeight, el.offsetHeight);
    }
    return Math.ceil(paddingTop + maxChildHeight + paddingBottom);
  }, [overviewFirstRowCount, isMobile]);

  const measureFullHeight = useCallback(() => {
    const grid = overviewGridRef.current;
    if (!grid) return 0;
    return Math.ceil(grid.scrollHeight);
  }, []);

  const updateOverviewHeight = useCallback(() => {
    const collapsed = measureCollapsedHeight();
    const full = measureFullHeight();
    setOverviewAnimatedHeight(showAllOverviewFrameworks ? full : collapsed);
  }, [measureCollapsedHeight, measureFullHeight, showAllOverviewFrameworks]);

  useEffect(() => {
    // Recompute on mount/resize and when frameworks change
    const onResize = () => {
      recomputeOverviewColumns();
      // next frame to ensure layout settled
      requestAnimationFrame(updateOverviewHeight);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeOverviewColumns, updateOverviewHeight, data?.frameworks?.length]);

  useEffect(() => {
    if (hasAutoSyncedRef.current) return;
    if (!tenancyId || tenancyId === 'Loading' || tenancyId === 'Loading...' || tenancyId === '') return;
    if (isLoading) return;
    const controls = data?.controls || [];
    if (!controls.length) return;
    const outOfSync = controls.filter((c) => {
      const desired = !(c.status === 'Not applicable' || c.status === 'Unknown');
      return (c.applicable as boolean) !== desired;
    });
    if (!outOfSync.length) {
      hasAutoSyncedRef.current = true;
      return;
    }
    (async () => {
      try {
        for (const c of outOfSync) {
          const desired = !(c.status === 'Not applicable' || c.status === 'Unknown');
          await updateControlStatus(tenancyId as string, { ...c, applicable: desired });
        }
        hasAutoSyncedRef.current = true;
        await refetch();
        toast.success(`Synchronized ${outOfSync.length} control${outOfSync.length === 1 ? '' : 's'}`);
      } catch (e) {
        console.error('[SOA] Auto-sync failed', e);
      }
    })();
  }, [tenancyId, isLoading, data?.controls, refetch]);

  // Ensure initial measurement before paint to reduce flicker
  useLayoutEffect(() => {
    updateOverviewHeight();
  }, [updateOverviewHeight]);

  // Rerun donut height calculation after skeleton disappears (first load)
  useEffect(() => {
    if (overviewTabValue !== 'donut') return;
    if (isLoading || loadingFrameworkNames) return;
    // Wait a tick to ensure cards are rendered
    const timeout = setTimeout(() => {
      recomputeOverviewColumns();
      updateOverviewHeight();
    }, 0);
    return () => clearTimeout(timeout);
  }, [isLoading, loadingFrameworkNames, overviewTabValue, recomputeOverviewColumns, updateOverviewHeight]);

  // Recompute height when toggle changes
  useEffect(() => {
    updateOverviewHeight();
  }, [showAllOverviewFrameworks, updateOverviewHeight]);

  // Ensure initial collapsed height after data loads (double rAF to wait for layout), only when Donuts is active
  useEffect(() => {
    if (overviewTabValue !== 'donut') return;
    if (!data?.frameworks?.length) return;
    if (showAllOverviewFrameworks) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      recomputeOverviewColumns();
      raf2 = window.requestAnimationFrame(() => {
        const collapsed = measureCollapsedHeight();
        if (collapsed > 0) {
          setOverviewAnimatedHeight(collapsed);
        } else {
          updateOverviewHeight();
        }
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [overviewTabValue, data?.frameworks?.length, showAllOverviewFrameworks, recomputeOverviewColumns, measureCollapsedHeight, updateOverviewHeight]);

  // When the Donuts tab becomes active, ensure we measure after layout and start collapsed.
  useEffect(() => {
    if (overviewTabValue !== 'donut') return;
    let raf1 = 0;
    let raf2 = 0;
    // Only force initial collapsed state on the first activation of Donuts
    if (!hasInitializedDonutRef.current) {
      hasInitializedDonutRef.current = true;
      setShowAllOverviewFrameworks(false);
    }
    raf1 = window.requestAnimationFrame(() => {
      // Recompute columns and then measure on the next frame to ensure DOM is ready
      recomputeOverviewColumns();
      raf2 = window.requestAnimationFrame(() => {
        const collapsed = measureCollapsedHeight();
        if (!showAllOverviewFrameworks && collapsed > 0) {
          setOverviewAnimatedHeight(collapsed);
        } else {
          updateOverviewHeight();
        }
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [overviewTabValue, showAllOverviewFrameworks, recomputeOverviewColumns, measureCollapsedHeight, updateOverviewHeight]);

  // Handle control edit
  const handleEditControl = (control: Control) => {
    setSelectedControl(control);
    setEditModalOpen(true);
  };

  // Handle control update
  const handleUpdateControl = async (updatedControl: Control) => {
    try {
      const companyID = tenancyId;

      // Call our service to update the control
      await updateControlStatus(companyID, updatedControl);

      toast.success(`Control ${updatedControl.controlID} has been updated successfully.`);

      setEditModalOpen(false);
      refetch();
    } catch (error) {
      console.error('Error updating control:', error);
      toast.error(`Failed to update control: ${(error as Error).message}`);
    }
  };

  // Create a memoized function to check if a control matches the framework filter
  const matchesFramework = useCallback(
    (control: Control): boolean => {
      if (!framework) return true;

      // Special handling for RFFR filter - show only ISM controls with isRFFR flag
      if (framework === 'rffr') {
        return control.frameworkId === 'ism' && control.isRFFR === true;
      }

      // Direct match by ID (most efficient check first)
      if (control.frameworkId === framework) return true;

      // Check resolved names (cached)
      const controlFrameworkName = resolvedFrameworkNames.get(control.frameworkId);
      const selectedFrameworkName = resolvedFrameworkNames.get(framework);

      if (controlFrameworkName && selectedFrameworkName) {
        if (controlFrameworkName.toLowerCase() === selectedFrameworkName.toLowerCase()) {
          return true;
        }
      }

      // Fallback to framework object comparison
      if (data) {
        const controlFramework = data.frameworks.find((f) => f.id === control.frameworkId);
        const selectedFramework = data.frameworks.find((f) => f.id === framework);

        if (controlFramework && selectedFramework && controlFramework.name.toLowerCase() === selectedFramework.name.toLowerCase()) {
          return true;
        }
      }

      return false;
    },
    [framework, resolvedFrameworkNames, data]
  );

  // Create a memoized function to check if a control matches the search term
  const matchesSearch = useCallback(
    (control: Control): boolean => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        (typeof control.id === 'string' && control.id.toLowerCase().includes(searchLower)) ||
        (typeof control.controlID === 'string' && control.controlID.toLowerCase().includes(searchLower)) ||
        (typeof control.name === 'string' && control.name.toLowerCase().includes(searchLower)) ||
        (typeof control.description === 'string' && control.description.toLowerCase().includes(searchLower)) ||
        (typeof control.justification === 'string' && control.justification.toLowerCase().includes(searchLower))
      );
    },
    [searchTerm]
  );

  // Create a memoized function to check if a control matches the status filter
  const matchesStatus = useCallback(
    (control: Control): boolean => {
      if (!implementationStatus) return true;
      return control.status === implementationStatus;
    },
    [implementationStatus]
  );

  // Create a memoized unique controls map
  const uniqueControlsMap = useMemo(() => {
    if (!data) return new Map<string, Control>();

    const uniqueMap = new Map<string, Control>();

    // Only iterate through controls once and apply all filters
    for (const control of data.controls) {
      if (matchesFramework(control) && matchesStatus(control) && matchesSearch(control)) {
        // Only add if it's not already in the map (prevents duplicates)
        if (!uniqueMap.has(control.controlID)) {
          uniqueMap.set(control.controlID, control);
        }
      }
    }

    return uniqueMap;
  }, [data, matchesFramework, matchesStatus, matchesSearch]);

  // Get filtered controls from the unique controls map
  const filteredControls = useMemo(() => {
    return Array.from(uniqueControlsMap.values());
  }, [uniqueControlsMap]);

  // Defensive: Only sort controls with a valid string controlID
  const safeFilteredControls = filteredControls.filter(c => typeof c.controlID === 'string');
  const unsafeFilteredControls = filteredControls.filter(c => typeof c.controlID !== 'string');

  const alphanumeric = (a, b) => {
    // A.5.1, A.5.11, ISM-1203, CC.AP.32, PA.123
    const aParts = a.controlID.split(/[^\w]+/);
    const bParts = b.controlID.split(/[^\w]+/);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] || '';
      const bVal = bParts[i] || '';
      const aNum = parseInt(aVal, 10);
      const bNum = parseInt(bVal, 10);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum !== bNum) return aNum - bNum;
      } else {
        if (aVal !== bVal) return aVal.localeCompare(bVal);
      }
    }
    return 0;
  };

  const sortedControls = useMemo(() => {
    const aDotControls = safeFilteredControls.filter(c => /^A\.\d+\.\d+$/.test(c.controlID));
    const rest = safeFilteredControls.filter(c => !/^A\.\d+\.\d+$/.test(c.controlID));
    return [
      ...aDotControls.sort(alphanumeric),
      ...rest.sort(alphanumeric),
      ...unsafeFilteredControls
    ];
  }, [safeFilteredControls, unsafeFilteredControls]);

  useEffect(() => {
    if (hasHandledControlParam) return;
    const requestedControlId = searchParams.get('control');
    if (!requestedControlId) return;
    if (isLoading) return;
    const controls = data?.controls || [];
    if (!controls.length) return;

    const normalized = requestedControlId.trim().toLowerCase();
    const matchingControl = controls.find(control =>
      typeof control.controlID === 'string' && control.controlID.trim().toLowerCase() === normalized
    );

    if (matchingControl) {
      setSelectedControl(matchingControl);
      setEditModalOpen(true);
      setHasHandledControlParam(true);
      return;
    }

    setSearchTerm(requestedControlId);
    setHasHandledControlParam(true);
  }, [searchParams, data?.controls, isLoading, hasHandledControlParam]);

  // DataTable handles pagination internally; legacy manual pagination removed

  // Handle export to CSV
  const handleExport = () => {
    try {
      if (!data || !data.controls || data.controls.length === 0) {
        toast.error('No data to export. There are no controls available to export.');
        return;
      }

      // Get all controls (use filteredControls if you want to respect current filters)
      const controlsToExport = sortedControls.length > 0 ? sortedControls : data.controls;

      // Create CSV header
      const headers = [
        'Control Name',
        'Control Number',
        'Control Framework',
        'Control Justification',
        'Control Implemented'
      ];

      // Create CSV rows
      const rows = controlsToExport.map(control => {
        const framework = data.frameworks.find(f => f.id === control.frameworkId);
        const frameworkName = framework?.name || 'Unknown Framework';

        return [
          `"${(control.name || '').replace(/"/g, '""')}"`,
          `"${(control.controlID || '').replace(/"/g, '""')}"`,
          `"${frameworkName.replace(/"/g, '""')}"`,
          `"${(control.justification || '').replace(/"/g, '""')}"`,
          `"${control.status || 'Not Specified'}"`
        ].join(',');
      });

      // Combine header and rows
      const csvContent = [
        headers.join(','),
        ...rows
      ].join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `SOA_Export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('The SOA controls have been exported successfully.');
    } catch (error) {
      console.error('Error exporting SOA:', error);
      toast.error('An error occurred while exporting the SOA controls.');
    }
  };

  const [helpContent, setHelpContent] = useState(null);
  useEffect(() => {
    getHelpContentByPage('soa').then(setHelpContent);
  }, []);

  // Loading skeleton for SOA component
  const SOASkeletonLoader = () => {
    return (
      <div className="page-container">
        <div className="glass-card">
          {/* Header skeleton */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="w-full sm:w-1/2">
              <Skeleton className="h-8 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>

          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="glass-card">
                <CardHeader className="pb-2">
                  <Skeleton className="h-6 w-1/2" />
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                    <div className="w-full sm:w-auto mb-3 sm:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Skeleton className="h-3 w-3 rounded-full" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <Skeleton className="h-3 w-3 rounded-full" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3 w-3 rounded-full" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </div>
                    <Skeleton className="h-20 w-20 sm:h-24 sm:w-24 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters skeleton */}
          <div className="flex flex-col gap-4 mb-6">
            <Skeleton className="h-10 w-full" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>

          {/* Table skeleton */}
          <div className="table-wrapper">
            <Table className="table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">
                    <Skeleton className="h-4 w-full" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-full" />
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <Skeleton className="h-4 w-full" />
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <Skeleton className="h-4 w-full" />
                  </TableHead>
                  <TableHead className="w-[80px]">
                    <Skeleton className="h-4 w-full" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-full mb-1" />
                      <Skeleton className="h-4 w-3/4" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  // Get status class (must be above all early returns)
  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'fully implemented':
        return 'status-enabled';
      case 'partially implemented':
        return 'status-partial';
      case 'not implemented':
        return 'status-disabled';
      case 'not applicable':
        return 'status-not-applicable';
      default:
        return 'status-unknown';
    }
  };

  // Memoize columns at the top-level before any early returns (fix hook order)
  const columns = useMemo(() => getSOAColumns({
    helpData: helpContent,
    frameworks: data.frameworks,
    userIdToName,
    getStatusClass,
    isReadOnly,
    onEdit: handleEditControl,
  }), [helpContent, data.frameworks, userIdToName, isReadOnly]);

  // Loading and error states
  if (isLoading || loadingFrameworkNames) {
    return <SOASkeletonLoader />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-destructive/10 p-6 rounded-lg max-w-md">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Data</h2>
          <p className="text-muted-foreground mb-4">{(error as Error).message}</p>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-muted p-6 rounded-lg max-w-md">
          <h2 className="text-xl font-semibold mb-2">No Data Available</h2>
          <p className="text-muted-foreground mb-4">There is no Statement of Applicability data available for this tenancy.</p>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }


  return (
    <div className={`page-container ${isDark ? 'dark' : ''}`}>
      <div className="glass-card">
        {/* Header */}
        <div className="header-row">
          <div>
            <h2 className="title flex flex-row">
              Statement of Applicability
              <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle">
              {data.version ? (
                <button
                  onClick={() => setVersionHistoryModalOpen(true)}
                  className="inline-flex items-center gap-1 hover:text-primary hover:underline transition-colors cursor-pointer bg-transparent border-none p-0 text-inherit font-inherit"
                >
                  Version {data.version.number}, last updated {data.version.lastUpdated.toLocaleDateString()}
                  <span className="text-xs text-muted-foreground">(View History)</span>
                </button>
              ) : (
                'Manage and track compliance controls across all frameworks'
              )}
            </p>
          </div>
          <div className="header-actions">
            <Button variant="outline" onClick={handleExport} className="flex-1 sm:flex-none justify-center">
              <Download className="h-4 w-4 mr-2" />
              <span className="whitespace-nowrap">Export Report</span>
            </Button>
            <Button variant="outline" onClick={() => refetch()} className="btn-outline flex-1 sm:flex-none justify-center dark:!text-white hover:bg-muted">
              {/* Make icon spin */}
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshLoading ? 'animate-spin' : ''}`} />
              <span className="whitespace-nowrap">{refreshLoading ? 'Refreshing...' : 'Refresh'}</span>
            </Button>
          </div>
        </div>

        {/* Overview Chart */}
        <Card className='mb-6 overflow-visible'>
          <Tabs value={overviewTabValue} onValueChange={(v) => setOverviewTabValue(v as 'donut' | 'bar')}>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="space-y-1">
                <CardTitle>Overview</CardTitle>
                <CardDescription>Overview of compliance controls across all frameworks.</CardDescription>
              </div>

              <TabsList className="shrink-0">
                <TabsTrigger value="donut">Details</TabsTrigger>
                <TabsTrigger value="bar">Graph</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="overflow-visible">
              <TabsContent value="bar">
                {(() => {
                  // Prepare chart data
                  const sortedFrameworks = [...data.frameworks].sort((a, b) =>
                    a.name.localeCompare(b.name)
                  )
                  const chartData = sortedFrameworks.map((framework) => {
                    const frameworkStats =
                      data.stats.find((s) => s.id === framework.id) || {
                        id: framework.id,
                        name: framework.name,
                        stats: {
                          implemented: 0,
                          partiallyImplemented: 0,
                          notImplemented: 0,
                          notApplicable: 0,
                          unknown: 0,
                        },
                      }

                    return {
                      name: framework.name,
                      fullyImplemented: frameworkStats.stats.implemented || 0,
                      partiallyImplemented: frameworkStats.stats.partiallyImplemented || 0,
                      notImplemented: frameworkStats.stats.notImplemented || 0,
                      notApplicable: frameworkStats.stats.notApplicable || 0,
                    }
                  })

                  // Chart config for ShadCN Chart (legend + tokens)
                  const chartConfig = {
                    fullyImplemented: {
                      label: 'Fully Implemented',
                      color: 'var(--color-fullyImplemented)',
                    },
                    partiallyImplemented: {
                      label: 'Partially Implemented',
                      color: 'var(--color-partiallyImplemented)',
                    },
                    notImplemented: {
                      label: 'Not Implemented',
                      color: 'var(--color-notImplemented)',
                    },
                    notApplicable: {
                      label: 'Not Applicable',
                      color: 'var(--color-notApplicable)',
                    },
                  } as ChartConfig

                  return (
                    <ChartContainer
                      config={chartConfig}
                      className="mx-auto w-full max-w-4xl h-[340px] aspect-auto overflow-visible"
                      // set CSS variables locally (or move to globals.css — see below)
                      style={
                        {
                          // core palette
                          ['--color-fullyImplemented' as any]: '#2ECC71',
                          ['--color-partiallyImplemented' as any]: '#E67E22',
                          ['--color-notImplemented' as any]: '#E74C3C',
                          ['--color-notApplicable' as any]: '#95A5A6',
                        } as React.CSSProperties
                      }
                    >
                      <BarChart
                        accessibilityLayer
                        data={chartData}
                        barCategoryGap={20}
                        stackOffset="expand"
                        layout="vertical"
                        barSize={24}
                        margin={{ top: 8, right: 12, left: 12, bottom: 80 }}
                      >
                        <XAxis
                          type="number"
                          domain={[0, 1]}
                          tickFormatter={(v) => `${Math.round((v as number) * 100)}%`}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          dataKey="name"
                          type="category"
                          tickLine={false}
                          axisLine={false}
                          width={140}
                          tickMargin={8}
                        />

                        <Bar
                          dataKey="fullyImplemented"
                          stackId="a"
                          fill="var(--color-fullyImplemented)"
                          shape={(props: any) => {
                            const { payload, ...rest } = props;
                            const others = (payload?.partiallyImplemented || 0) + (payload?.notImplemented || 0) + (payload?.notApplicable || 0);
                            const radius = others === 0 ? [4, 4, 4, 4] : [4, 0, 0, 4];
                            return <Rectangle {...rest} radius={radius} fill="var(--color-fullyImplemented)" />;
                          }}
                        />
                        <Bar
                          dataKey="partiallyImplemented"
                          stackId="a"
                          fill="var(--color-partiallyImplemented)"
                        />
                        <Bar
                          dataKey="notImplemented"
                          stackId="a"
                          fill="var(--color-notImplemented)"
                        />
                        <Bar
                          dataKey="notApplicable"
                          stackId="a"
                          fill="var(--color-notApplicable)"
                          radius={[0, 4, 4, 0]}
                        />

                        <ChartTooltip
                          content={<ChartTooltipContent indicator="line" className="bg-card" />}
                          cursor={false}
                          defaultIndex={1}
                        />
                      </BarChart>
                    </ChartContainer>
                  )
                })()}
              </TabsContent>
              <TabsContent value="donut">
                <div
                  ref={overviewWrapperRef}
                  className="mb-6"
                  style={{
                    // Use measured height when available; otherwise allow auto so content renders
                    // Add extra padding to account for card scale-up on hover (1.02x = ~2% overflow)
                    height: overviewAnimatedHeight > 0 ? `${overviewAnimatedHeight + 16}px` : undefined,
                    overflow: 'visible',
                    transition: 'height 360ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  <div className="grid gap-4 p-2" ref={overviewGridRef}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                      alignItems: 'stretch',
                      margin: '-0.5rem',
                    }}
                  >
                    {[...data.frameworks]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((fw) => {
                        const frameworkStats = data.stats.find(s => s.id === fw.id) || {
                          id: fw.id,
                          name: fw.name,
                          stats: {
                            implemented: 0,
                            partiallyImplemented: 0,
                            notImplemented: 0,
                            notApplicable: 0,
                            unknown: 0
                          }
                        };

                        const isSelected = framework === fw.id;

                        return (
                          <Card
                            key={fw.id}
                            onClick={() => setFramework(prev => prev === fw.id ? null : fw.id)}
                            style={{
                              minWidth: '350px',
                              background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : undefined,
                              backdropFilter: 'blur(8px)'
                            }}
                            className={`relative overflow-hidden cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                              isSelected
                                ? 'border-2 border-primary shadow-lg'
                                : 'glass-card hover:shadow-md'
                            }`}
                          >
                            {/* Light accent when selected */}
                            {isSelected && (
                              <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-primary/10 pointer-events-none" />
                            )}

                            {/* Selection indicator */}
                            {isSelected && (
                              <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                                <Check className="h-3 w-3 text-primary-foreground" />
                              </div>
                            )}

                            <CardHeader className="pb-2 relative z-10">
                              <CardTitle className="text-lg">{fw.name}</CardTitle>
                            </CardHeader>
                            <CardContent className="relative z-10">
                              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                                <div className="w-full sm:w-auto mb-3 sm:mb-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="h-3 w-3 rounded-full bg-green-500"></span>
                                    <span className="text-sm">Fully Implemented: {frameworkStats.stats.implemented || 0}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="h-3 w-3 rounded-full bg-amber-500"></span>
                                    <span className="text-sm">Partially Implemented: {frameworkStats.stats.partiallyImplemented || 0}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="h-3 w-3 rounded-full bg-red-500"></span>
                                    <span className="text-sm">Not Implemented: {frameworkStats.stats.notImplemented || 0}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-full bg-gray-500"></span>
                                    <span className="text-sm">Not Applicable: {frameworkStats.stats.notApplicable || 0}</span>
                                  </div>
                                </div>
                                <div className="h-20 w-20 sm:h-24 sm:w-24 mx-auto sm:mx-0">
                                  <div className="h-full w-full rounded-full border-4 border-gray-200 flex items-center justify-center">
                                    <div className="text-base sm:text-lg font-bold">
                                      {(() => {
                                        const total = (frameworkStats.stats.implemented || 0) +
                                          (frameworkStats.stats.partiallyImplemented || 0) +
                                          (frameworkStats.stats.notImplemented || 0) +
                                          (frameworkStats.stats.notApplicable || 0);
                                        const implemented = frameworkStats.stats.implemented || 0;
                                        return total > 0 ? Math.round((implemented / total) * 100) : 0;
                                      })()}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </div>

                {data.frameworks.length > (overviewFirstRowCount || (isMobile ? 1 : 3)) && (
                  <div className="flex justify-center">
                    <Button variant="outline" onClick={() => setShowAllOverviewFrameworks((prev) => !prev)}>
                      {showAllOverviewFrameworks ? 'Show Less' : 'Show More'}
                    </Button>
                  </div>
                )}
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* Filters */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="relative w-full">
            <Input placeholder="Search controls..." className="pl-8 mb-2" value={searchTerm} onChange={(e) => {
              setSearchTerm(e.target.value);
            }} />


  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
    <Select value={framework || 'all'} onValueChange={(value) => {
      setFramework(value === 'all' ? null : value);
    }}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="All Frameworks" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Frameworks</SelectItem>
        {data.frameworks.map((fw) => (
          <SelectItem key={fw.id} value={fw.id}>
            {fw.name}
          </SelectItem>
        ))}
        {/* Add RFFR as a virtual filter option if ISM is enabled */}
        {data.frameworks.some(fw => fw.id === 'ism') && (
          <SelectItem value="rffr">RFFR (ISM Subset)</SelectItem>
        )}
      </SelectContent>
    </Select>

            <Select value={implementationStatus || 'all'} onValueChange={(value) => {
              setImplementationStatus(value === 'all' ? null : value);
            }}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Fully implemented">Fully Implemented</SelectItem>
                <SelectItem value="Partially implemented">Partially Implemented</SelectItem>
                <SelectItem value="Not implemented">Not Implemented</SelectItem>
                <SelectItem value="Not applicable">Not Applicable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          </div>
        </div>
        {/* DataTable (handles both desktop and mobile via card view) */}
        <div className="w-full overflow-auto">
          <DataTable
            columns={columns}
            data={sortedControls}
            pageSize={10}
            userIdToName={userIdToName}
            onRowClick={(row) => handleEditControl(row as Control)}
            pageIndex={tablePageIndex}
            onPageIndexChange={setTablePageIndex}
          />
        </div>
      </div>

      {/* Edit Modal */}
      <ControlEditModal
        control={selectedControl}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleUpdateControl}
        isReadOnly={isReadOnly}
      />

      {/* Version History Modal */}
      <SOAVersionHistoryModal
        isOpen={versionHistoryModalOpen}
        onClose={() => setVersionHistoryModalOpen(false)}
      />
    </div >
  );
};

export default SOA;
